import { graphql } from "gatsby";
import React, { useState } from "react";
import { useEffect } from "react";
import { Colours } from "../../../utils/colours";
import { getSupportWidgetScript } from "../../../utils/scripts";
import WrappedSection from "../../WrappedSection/WrappedSection";
import * as styles from "./ParagraphSupportWidget.module.scss";

export interface ParagraphSupportWidgetProps {
	heading?: string;
	text?: string;
	embedCode: string;
	theme: string;
	sectionColour?: string;
	jumpLinksTitle?: string;
}

export const getParagraphSupportWidgetProps = ({
	field_embed_code,
	field_title,
	field_widget_text,
	field_widget_theme = "alternative",
	field_jump_links_title,
}: Queries.ParagraphSupportWidgetDataFragment): ParagraphSupportWidgetProps => ({
	embedCode: field_embed_code,
	heading: field_title,
	text: field_widget_text,
	theme: field_widget_theme,
	sectionColour: Colours.WHITE,
	jumpLinksTitle: field_jump_links_title,
});
export const ParagraphSupportWidget: React.FC<ParagraphSupportWidgetProps> = ({
	heading,
	embedCode,
	text,
	theme,
	sectionColour = Colours.WHITE,
	jumpLinksTitle,
}) => {
	const [hasCalledIframeResizer, setHasCalledIframeResizer] = useState(false);
	useEffect(() => {
		const callIframeResizer = () => {
			if (hasCalledIframeResizer) return;
			if (window.iFrameResize) {
				window.iFrameResize.call();
				setHasCalledIframeResizer(true);
			}
		};
		let interval: NodeJS.Timeout = null;
		if (!hasCalledIframeResizer) {
			interval = setInterval(callIframeResizer, 500);
		} else {
			clearInterval(interval);
		}
		return () => clearInterval(interval);
	}, [hasCalledIframeResizer]);

	const getThemeColour = () => {
		switch (theme) {
			case "alternative":
				return Colours.SANDY_FUR;
			case "light":
				return Colours.WHITE;
			case "primary":
				return Colours.SUNSHINE_YELLOW;
			default:
				return Colours.WHITE;
		}
	};
	return (
		<WrappedSection
			sectionName="ParagraphSupportWidget"
			sectionColour={sectionColour}
		>
			<div
				className={styles.inner}
				style={{ backgroundColor: getThemeColour() }}
			>
				{heading && (
					<div
						className={
							theme === "alternative"
								? styles.title
								: theme === "primary"
									? styles.title
									: styles.titlenopadding
						}
					>
						{jumpLinksTitle ? (
							<h2 data-jumplinkstitle={jumpLinksTitle}>{heading}</h2>
						) : (
							<h2>{heading}</h2>
						)}
					</div>
				)}
				{text && (
					<div className={styles.description}>
						<p>{text}</p>
					</div>
				)}
				<div
					className={styles.widget}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
					dangerouslySetInnerHTML={{
						__html: getSupportWidgetScript(embedCode),
					}}
				/>
			</div>
		</WrappedSection>
	);
};

export default ParagraphSupportWidget;

export const fragment = graphql`
    fragment ParagraphSupportWidgetData on paragraph__support_widget {
        __typename
        id
        field_title
		field_jump_links_title
        field_widget_text
        field_embed_code
        field_widget_theme
    }
`;
